import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { AtSign, Inbox, Package2, ArrowRight, UserPlus, ShieldAlert, Bell, BellOff, BellRing, CalendarClock } from "lucide-react";
import { toast } from "sonner";
import { STAGE_COLORS, STAGE_PATHS } from "@/components/dashboard/utils";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ── Web (desktop/mobile) notifications for @mentions ───────────────────────
// Browser-native, per-browser-and-device. Everything below is written to fail
// SAFE: on any browser that lacks the API (older embedded webviews, iOS Safari
// outside an installed PWA, insecure origins) it degrades to "unsupported" and
// never throws — the in-app toast path is untouched.
//
// A tab-scoped custom event ("rl-open-mentions") bridges the plain-DOM
// notification onclick handler back into React, so clicking the OS banner can
// open the popover and flash the relevant row.
const NOTIF_SUPPORTED = typeof window !== "undefined" && "Notification" in window;
const OPEN_MENTIONS_EVENT = "rl-open-mentions";

// Current browser permission: "unsupported" | "default" | "granted" | "denied".
function readNotifPerm() {
  if (!NOTIF_SUPPORTED) return "unsupported";
  try {
    return window.Notification.permission || "default";
  } catch {
    return "unsupported";
  }
}

// Small palette-driven chip for a mention's current stage. Borrows the exact
// stage colour from the shared dashboard palette so it matches stage chips
// elsewhere in the app.
function StageChip({ stage }) {
  if (!stage) return null;
  const meta = STAGE_COLORS[stage];
  const color = meta?.color || "hsl(var(--muted-foreground))";
  const bg = meta?.bg || "hsl(var(--muted))";
  return (
    <span
      className="inline-flex items-center gap-1 text-[10px] font-semibold px-1.5 py-px rounded-full leading-none whitespace-nowrap"
      style={{ backgroundColor: bg, color }}
    >
      <span className="w-[5px] h-[5px] rounded-full shrink-0" style={{ backgroundColor: color }} />
      {stage}
    </span>
  );
}

function formatRelative(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    const diffMs = Date.now() - d.getTime();
    const mins = Math.round(diffMs / 60000);
    if (mins < 1) return "just now";
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.round(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    const days = Math.round(hrs / 24);
    if (days < 7) return `${days}d ago`;
    return d.toLocaleDateString(undefined, { day: "2-digit", month: "short", year: "2-digit" });
  } catch { return ""; }
}

export default function MentionInbox() {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [mentions, setMentions] = useState({ unread: [], read: [], unread_count: 0 });
  const firstPollRef = useRef(true);
  const shownToastsRef = useRef(new Set());
  // Browser notification permission for THIS browser/device (not account-wide).
  const [notifPerm, setNotifPerm] = useState(readNotifPerm);
  const notifPermRef = useRef(notifPerm);
  useEffect(() => { notifPermRef.current = notifPerm; }, [notifPerm]);
  // Id of the mention to briefly flash after a notification-click opens the panel.
  const [flashId, setFlashId] = useState(null);
  const flashTimerRef = useRef(null);

  // Build + show one browser notification for a mention. No-ops safely unless
  // the API exists and permission is granted. Clicking it focuses this tab and
  // asks the popover to open + flash the row (via the tab-scoped event).
  const fireMentionNotification = useCallback((m) => {
    try {
      if (notifPermRef.current !== "granted" || !NOTIF_SUPPORTED) return;
      const title = `${m.mentioned_by || "Someone"} mentioned you`;
      const ref = (m.item_ref || m.current_part_number || m.part_number || "").trim();
      const body = [(m.text_snippet || "").slice(0, 120), ref && `· ${ref}`]
        .filter(Boolean).join(" ");
      const n = new window.Notification(title, {
        body: body || "You have a new mention in RockLink.",
        icon: "/favicon-192.png",
        tag: `rl-mention-${m.id}`, // collapses repeats of the same mention in the OS tray
      });
      n.onclick = () => {
        try { window.focus(); } catch { /* noop */ }
        try {
          window.dispatchEvent(new CustomEvent(OPEN_MENTIONS_EVENT, { detail: { id: m.id } }));
        } catch { /* noop */ }
        try { n.close(); } catch { /* noop */ }
      };
    } catch { /* browser refused / offline — silently skip */ }
  }, []);

  const fetchMentions = useCallback(async (opts = {}) => {
    try {
      const res = await axios.get(`${API}/auth/mentions`);
      const data = res.data || { unread: [], read: [], unread_count: 0 };
      setMentions(data);
      if (!firstPollRef.current && opts.announce !== false) {
        for (const m of data.unread || []) {
          if (!shownToastsRef.current.has(m.id)) {
            shownToastsRef.current.add(m.id);
            toast(`${m.mentioned_by} mentioned you · ${m.item_ref || ""}`.trim(), {
              description: (m.text_snippet || "").slice(0, 100),
            });
            // Also raise a browser notification (per user's choice: always, even
            // when the tab is focused). Same shownToastsRef guard means each
            // mention fires at most once, and the firstPollRef gate above means
            // the existing backlog never fires on load. Wrapped so a browser
            // quirk can never break the poll.
            fireMentionNotification(m);
          }
        }
      } else {
        for (const m of data.unread || []) shownToastsRef.current.add(m.id);
      }
      firstPollRef.current = false;
    } catch { /* noop */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    fetchMentions({ announce: false });
    const id = setInterval(() => fetchMentions(), 60000);
    return () => clearInterval(id);
  }, [fetchMentions]);

  // Bridge from the (plain-DOM) notification onclick into React: open the
  // popover and flash the clicked mention. Also re-read the permission when the
  // window regains focus — the user may have changed it in browser settings,
  // or accepted the prompt in another surface.
  useEffect(() => {
    const onOpenRequest = (e) => {
      const id = e?.detail?.id;
      setOpen(true);
      fetchMentions({ announce: false });
      if (id) triggerFlash(id);
    };
    const onFocus = () => setNotifPerm(readNotifPerm());
    window.addEventListener(OPEN_MENTIONS_EVENT, onOpenRequest);
    window.addEventListener("focus", onFocus);
    return () => {
      window.removeEventListener(OPEN_MENTIONS_EVENT, onOpenRequest);
      window.removeEventListener("focus", onFocus);
      if (flashTimerRef.current) clearTimeout(flashTimerRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchMentions]);

  // Flash a mention row for ~1.4s (matches the rl-mention-flash keyframe), then
  // scroll it into view. Self-clears so it can re-fire on the next click.
  const triggerFlash = useCallback((id) => {
    setFlashId(null);
    // next tick so React re-applies the class even if it's the same id
    requestAnimationFrame(() => {
      setFlashId(id);
      if (flashTimerRef.current) clearTimeout(flashTimerRef.current);
      flashTimerRef.current = setTimeout(() => setFlashId(null), 1500);
      requestAnimationFrame(() => {
        try {
          document.querySelector(`[data-testid="mention-item-${id}"]`)
            ?.scrollIntoView({ block: "nearest", behavior: "smooth" });
        } catch { /* noop */ }
      });
    });
  }, []);

  // Ask the browser for permission. Only meaningful from "default" — the native
  // prompt requires a user gesture (this is called from a button click). If the
  // user grants it, drain the current unread so we don't blast old mentions.
  const requestNotifPermission = useCallback(async () => {
    if (!NOTIF_SUPPORTED) return;
    try {
      const result = await window.Notification.requestPermission();
      setNotifPerm(result || readNotifPerm());
    } catch {
      setNotifPerm(readNotifPerm());
    }
  }, []);

  const markAllRead = async () => {
    try {
      await axios.post(`${API}/auth/mentions/read-all`);
      setMentions(prev => ({
        unread: [],
        read: [...prev.unread.map(x => ({ ...x, read: true })), ...prev.read].slice(0, 20),
        unread_count: 0,
      }));
    } catch { /* noop */ }
  };

  // Click a mention → jump to the item's current stage and auto-search for it.
  // Relies on StagePage's ?q=<part> deep-link bridge, which pushes the value
  // into that stage's search bar. Degrades gracefully: if the parent item was
  // deleted (no current_stage), the row stays non-clickable.
  const goToItem = (m) => {
    // Client-creation requests have no parent item — they route straight to
    // the PO Received page, where the "Approve new clients" button lives.
    // Requests with no parent item route straight to the page that reviews
    // them: clients to PO Received, machines to Maintenance.
    const isClientReq = m.type === "client_request";
    const isMachineReq = m.type === "machine_request";
    const standalone = isClientReq || isMachineReq;
    const path = isClientReq ? "/po-received" : isMachineReq ? "/maintenance" : STAGE_PATHS[m.current_stage];
    const query = (m.current_part_number || m.part_number || "").trim();
    if (!path || (!standalone && !query)) return;
    // Optimistically mark this one read so the badge/count reflects the click.
    if (!m.read) {
      axios.post(`${API}/auth/mentions/${m.id}/read`).catch(() => {});
      setMentions(prev => {
        const unread = (prev.unread || []).filter(x => x.id !== m.id);
        const moved = (prev.unread || []).find(x => x.id === m.id);
        return {
          unread,
          read: moved ? [{ ...moved, read: true }, ...(prev.read || [])].slice(0, 20) : (prev.read || []),
          unread_count: Math.max(0, (prev.unread_count || 0) - 1),
        };
      });
    }
    setOpen(false);
    navigate(standalone ? path : `${path}?q=${encodeURIComponent(query)}`);
  };

  // Auto-mark-all-read when popover closes (mirrors bell inbox UX).
  // Opening refetches so a ping from seconds ago shows without waiting
  // for the 60s poll.
  const handleOpenChange = (next) => {
    setOpen(next);
    if (next) fetchMentions({ announce: false });
    if (!next && (mentions.unread_count || 0) > 0) {
      markAllRead();
    }
  };

  const count = mentions.unread_count || 0;
  const allMentions = [...(mentions.unread || []), ...(mentions.read || [])];

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className={`relative inline-flex items-center justify-center w-8 h-8 rounded-full transition-[transform,colors,box-shadow] duration-150 ${
            open
              ? "bg-zinc-200 dark:bg-zinc-800 text-[hsl(var(--muted-foreground))] [box-shadow:inset_0_1px_3px_rgba(0,0,0,0.16),inset_0_0_0_1px_rgba(0,0,0,0.06)]"
              : count > 0
                ? "text-amber-600 rl-frost-btn"
                : "text-[hsl(var(--muted-foreground))] rl-frost-btn hover:text-[hsl(var(--foreground))]"
          }`}
          title={count > 0 ? `${count} new mention${count === 1 ? "" : "s"}` : "Mentions"}
          data-testid="mention-inbox-trigger"
        >
          <AtSign className="w-3.5 h-3.5" strokeWidth={2.2} />
          {count > 0 && (
            <span className="absolute -top-0.5 -right-0.5 inline-flex items-center justify-center min-w-[16px] h-[16px] px-1 rounded-full bg-red-500 text-white text-[9px] font-bold leading-none shadow-sm">
              {count > 99 ? "99+" : count}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        sideOffset={8}
        className="z-50 p-0 w-[360px] max-h-[70vh] overflow-hidden rounded-xl rl-frost border-[hsl(var(--border))] flex flex-col"
        data-testid="mention-inbox-panel"
      >
        <div className="px-3 pt-3 pb-2.5 flex items-center justify-between gap-2">
          <span className="inline-flex items-center gap-2">
            <AtSign className="w-4 h-4 shrink-0 text-orange-500" />
            <span className="text-sm font-semibold text-[hsl(var(--foreground))]">Mentions</span>
          </span>
          {count > 0 && (
            <button
              type="button"
              onClick={markAllRead}
              className="text-[11px] font-medium text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))] transition-colors"
              data-testid="mention-inbox-read-all"
            >
              Mark all read
            </button>
          )}
        </div>

        <div className="flex-1 overflow-y-auto">
          {allMentions.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-10 px-6 text-center text-[hsl(var(--muted-foreground))]">
              <Inbox className="w-8 h-8 opacity-30 mb-2" strokeWidth={1.2} />
              <p className="text-sm">No mentions yet</p>
              <p className="text-[11px] mt-0.5">You'll see pings here when teammates tag you.</p>
            </div>
          ) : (
            allMentions.map(m => {
              const isClientReq = m.type === "client_request";
              const isMachineReq = m.type === "machine_request";
              const navigable = isClientReq || isMachineReq || !!(STAGE_PATHS[m.current_stage] && (m.current_part_number || m.part_number));
              return (
              /* Clickable when the parent item is still live on a stage —
                 jumps to that stage and auto-searches for the part. */
              <div
                key={m.id}
                role={navigable ? "button" : undefined}
                tabIndex={navigable ? 0 : undefined}
                onClick={navigable ? () => goToItem(m) : undefined}
                onKeyDown={navigable ? (e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); goToItem(m); } } : undefined}
                className={`px-3 py-2.5 border-b border-[hsl(var(--border))]/40 transition-colors ${
                  m.read ? "opacity-70" : "bg-amber-500/5"
                } ${flashId === m.id ? "rl-mention-flash" : ""} ${navigable ? "group cursor-pointer hover:bg-[hsl(var(--muted))]/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-orange-500" : ""}`}
                title={navigable ? (isClientReq ? "Review on the PO Received page" : isMachineReq ? "Review in Maintenance" : `Open in ${m.current_stage} and find this item`) : undefined}
                data-testid={`mention-item-${m.id}`}
              >
                <div className="flex items-center gap-2 text-[10px] uppercase tracking-wider text-[hsl(var(--muted-foreground))]">
                  <span className="inline-flex items-center gap-1">
                    <span className="font-semibold normal-case text-[hsl(var(--foreground))]">{m.mentioned_by}</span>
                    {m.mentioned_by_role && <span className="normal-case">· {m.mentioned_by_role}</span>}
                  </span>
                  <span className="ml-auto tabular-nums">{formatRelative(m.created_at)}</span>
                  {navigable && (
                    <ArrowRight
                      className="w-3.5 h-3.5 shrink-0 text-orange-500 opacity-0 -translate-x-1 transition-all duration-200 ease-out group-hover:opacity-100 group-hover:translate-x-0 group-focus-visible:opacity-100 group-focus-visible:translate-x-0 motion-reduce:transition-none"
                      strokeWidth={2.4}
                      aria-hidden="true"
                    />
                  )}
                </div>
                <p className="mt-1 text-[12.5px] text-[hsl(var(--foreground))] line-clamp-2 leading-snug">
                  {m.text_snippet}
                </p>
                {/* Current stage of the parent line item / client-request tag */}
                {isClientReq ? (
                  <div className="mt-1.5">
                    <span className="inline-flex items-center gap-1 text-[10px] font-semibold px-1.5 py-px rounded-full leading-none whitespace-nowrap bg-amber-500/15 text-amber-600 dark:text-amber-400">
                      <UserPlus className="w-2.5 h-2.5" /> Client request
                    </span>
                  </div>
                ) : m.type === "block" ? (
                  <div className="mt-1.5 flex items-center gap-1.5">
                    <span className="inline-flex items-center gap-1 text-[10px] font-semibold px-1.5 py-px rounded-full leading-none whitespace-nowrap bg-red-500/10 border border-red-500/30 text-red-600 dark:text-red-400">
                      <ShieldAlert className="w-2.5 h-2.5" /> Blocked — waiting on you
                    </span>
                    {m.current_stage && <StageChip stage={m.current_stage} />}
                  </div>
                ) : m.type === "bom_eta" ? (
                  <div className="mt-1.5 flex items-center gap-1.5">
                    <span className="inline-flex items-center gap-1 text-[10px] font-semibold px-1.5 py-px rounded-full leading-none whitespace-nowrap bg-amber-500/10 border border-amber-500/30 text-amber-700 dark:text-amber-400">
                      <CalendarClock className="w-2.5 h-2.5" /> Child part ETA revised
                    </span>
                    {m.current_stage && <StageChip stage={m.current_stage} />}
                  </div>
                ) : m.current_stage && (
                  <div className="mt-1.5">
                    <StageChip stage={m.current_stage} />
                  </div>
                )}
                {/* Part # + PO # + line # reference row */}
                {(m.current_part_number || m.part_number || m.po_number) && (
                  <div className="mt-1.5 flex items-center gap-1.5 text-[10px] text-[hsl(var(--muted-foreground))]">
                    <Package2 className="w-3 h-3 shrink-0" />
                    <span
                      className="font-mono-data select-all cursor-text bg-[hsl(var(--muted))]/40 px-1 rounded text-[hsl(var(--foreground))]"
                      title="Part number — triple-click to copy"
                      onClick={(e) => e.stopPropagation()}
                    >
                      {m.current_part_number || m.part_number}
                    </span>
                    {(m.po_number || m.current_line_number) && (
                      <span className="text-[hsl(var(--muted-foreground))]/60 flex items-center gap-1">
                        {m.po_number && (
                          <><span className="opacity-50">·</span> <span className="font-mono-data">{m.po_number}</span></>
                        )}
                        {m.current_line_number && (
                          <><span className="opacity-50">·</span> <span className="font-mono-data">Ln {m.current_line_number}</span></>
                        )}
                      </span>
                    )}
                  </div>
                )}
              </div>
              );
            })
          )}
        </div>

        {/* ── Desktop-alert status + enable control (this browser only) ──── */}
        <NotifStatusRow perm={notifPerm} onEnable={requestNotifPermission} />
      </PopoverContent>
    </Popover>
  );
}

// Footer row inside the mentions popover showing whether desktop alerts are on
// for THIS browser, with a one-click enable when the browser hasn't been asked.
// Built to the self-evident bar: every state is visible and labelled, and the
// states that can't act (denied / unsupported) stay shown but greyed WITH a
// reason — never a dead button.
function NotifStatusRow({ perm, onEnable }) {
  let icon, label, hint, action = null;
  if (perm === "granted") {
    icon = <BellRing className="w-3.5 h-3.5 text-emerald-500 shrink-0" strokeWidth={2.2} />;
    label = "Desktop alerts on";
    hint = "You'll get a notification on this device when you're tagged.";
  } else if (perm === "denied") {
    icon = <BellOff className="w-3.5 h-3.5 text-[hsl(var(--muted-foreground))] shrink-0" strokeWidth={2.2} />;
    label = "Alerts blocked";
    hint = "Notifications are blocked for this site. Re-enable them in your browser's site settings, then reopen this menu.";
  } else if (perm === "default") {
    icon = <Bell className="w-3.5 h-3.5 text-amber-500 shrink-0" strokeWidth={2.2} />;
    label = "Desktop alerts off";
    hint = "Get pinged on this device the moment someone tags you.";
    action = (
      <button
        type="button"
        onClick={onEnable}
        className="rl-lit-btn shrink-0 text-[11px] font-semibold px-2.5 py-1 rounded-full bg-amber-500 text-white hover:bg-amber-600 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-500/50"
        data-testid="mention-notif-enable"
      >
        Enable
      </button>
    );
  } else {
    // unsupported
    icon = <BellOff className="w-3.5 h-3.5 text-[hsl(var(--muted-foreground))] shrink-0" strokeWidth={2.2} />;
    label = "Alerts not supported";
    hint = "This browser can't show desktop alerts. On iPhone, add RockLink to your Home Screen to enable them.";
  }

  return (
    <div
      className="border-t border-[hsl(var(--border))] px-3 py-2.5 flex items-center gap-2.5"
      title={hint}
      data-testid="mention-notif-status"
      data-perm={perm}
    >
      {icon}
      <div className="min-w-0 flex-1">
        <div className={`text-[12px] font-medium leading-tight ${
          perm === "granted" ? "text-[hsl(var(--foreground))]"
          : perm === "default" ? "text-[hsl(var(--foreground))]"
          : "text-[hsl(var(--muted-foreground))]"
        }`}>
          {label}
        </div>
        <div className="text-[10.5px] text-[hsl(var(--muted-foreground))] leading-snug mt-0.5 line-clamp-2">
          {hint}
        </div>
      </div>
      {action}
    </div>
  );
}
